// Service landing pages: point to point, hourly and flat rate. Copy only; every price is read from
// prices.json through prices.js. A page whose prices are missing is left out (see the filter below).
import { BOOK_HOURLY, BOOK_POINT, EXTRAS, INCLUDED } from './common';
import { HOURLY, POINT_RATES, VEHICLES, cheapest, hourlyRows, money, route, rows } from './prices';

const AREA = ['Manhattan', 'Brooklyn', 'Queens', 'The Bronx', 'Staten Island', 'JFK Airport', 'LaGuardia Airport', 'Newark Airport'];

const pointRows = rows([
  ['midtown-ues', 'Times Square → Upper East Side'],
  ['midtown-financial-district', 'Times Square → Financial District'],
  ['midtown-williamsburg', 'Times Square → Williamsburg, Brooklyn'],
  ['midtown-downtown-brooklyn', 'Times Square → Downtown Brooklyn'],
  ['greenwich-ct', 'Times Square → Greenwich, CT'],
]);
// "From" is the minimum fare, the lowest any point-to-point ride can cost
const pointFrom = POINT_RATES?.sedan && POINT_RATES?.suv && {
  sedan: POINT_RATES.sedan.minimum,
  suv: POINT_RATES.suv.minimum,
};

const flatRows = rows([
  ['jfk-manhattan', 'JFK Airport → Manhattan'],
  ['manhattan-jfk', 'Manhattan → JFK Airport'],
  ['lga-manhattan', 'LaGuardia (LGA) → Manhattan'],
  ['manhattan-lga', 'Manhattan → LaGuardia (LGA)'],
  ['ewr-manhattan', 'Newark (EWR) → Manhattan'],
  ['manhattan-ewr', 'Manhattan → Newark (EWR)'],
]);
const flatFrom = flatRows.length && {
  sedan: Math.min(...flatRows.map((r) => r.sedan)),
  suv: Math.min(...flatRows.map((r) => r.suv)),
};

const sedanRate = POINT_RATES?.sedan;
const suvRate = POINT_RATES?.suv;
const lga = route('lga-manhattan');
const jfk = route('jfk-manhattan');

const PAGES = [
  pointFrom && pointRows.length && sedanRate && suvRate && {
    path: '/point-to-point-car-service',
    linkText: 'Point-to-point car service',
    eyebrow: 'Point-to-point car service · NYC',
    title: <>Point-to-point black car service <em>in NYC</em></>,
    lead: 'A private chauffeur from your address straight to theirs, anywhere in New York City and beyond. Your total is shown before you book, with no meter running.',
    heroImage: 'manhattan-street-night',
    from: pointFrom,
    book: BOOK_POINT,
    cta: 'Book a point-to-point ride',
    introEyebrow: 'How point to point works',
    introTitle: <>One ride, <em>one price</em></>,
    intro: [
      'Point to point is the simplest way to ride with RoolDrive: you give us a pickup address, a drop-off address and a time, and a professional chauffeur takes you directly from one to the other in a private Sedan or SUV. It is the black car service New Yorkers use for dinner reservations, client meetings, hospital appointments, weddings, the theater, or getting home with shopping bags when the subway is not an option.',
      `The price depends on the distance of your route. A Sedan starts at ${money(sedanRate.base)} plus ${money(sedanRate.perMile)} per mile, and an SUV at ${money(suvRate.base)} plus ${money(suvRate.perMile)} per mile. You never have to work it out yourself: as soon as you enter both addresses, the booking page shows the total for each vehicle, and that is what you pay, plus any tip you choose to add.`,
      'Unlike a taxi or a ride-hailing app, there is no surge pricing at the curb and no meter ticking in traffic. You book ahead, the price is set, and your chauffeur is assigned to your ride in advance.',
    ],
    facts: [
      ['Sedan', `${money(sedanRate.base)} + ${money(sedanRate.perMile)}/mile`],
      ['SUV', `${money(suvRate.base)} + ${money(suvRate.perMile)}/mile`],
      ['Free waiting', '15 minutes'],
      ['Book', 'Online, in the app or by phone, 24/7'],
    ],
    priceTitle: <>Sample <em>fares</em> from Midtown</>,
    priceLead: 'Real quotes for common rides from Times Square. Your own route is priced the same way: by distance, shown before you book.',
    priceTables: [{ caption: 'Point-to-point fares from Times Square', rows: pointRows }],
    priceNote: 'Sample fares use typical road distances from Times Square.',
    sections: [
      {
        eyebrow: 'Stops on the way',
        title: <>Add a stop, <em>see the price</em></>,
        paragraphs: [
          'Need to pick up a colleague in SoHo before heading to Brooklyn, or drop a bag at home on the way to dinner? Add each stop when you book. The extra distance and the stop are included in the price you see before you pay, so nothing changes after the ride.',
          'If your plans have more than one or two stops, or you are not sure how long you will be at each one, an hourly chauffeur is usually better value: you keep the same car and chauffeur for the whole time, and stops are free.',
        ],
        image: 'night-ride-rear-seat',
        imageAlt: 'Rear seat of a RoolDrive car at night',
      },
      {
        eyebrow: 'Sedan or SUV',
        title: <>Choose the <em>right car</em></>,
        paragraphs: [
          'The Luxury Sedan seats up to 3 passengers with 3 large bags and suits most rides in the city. The Luxury SUV seats up to 6 passengers with 6 large bags, which makes it the choice for families, groups going out together and anyone with luggage, strollers or equipment.',
          'Both come with complimentary water, Wi-Fi and a phone charger, and whichever you choose, your chauffeur helps with your bags.',
        ],
        list: ['Up to 3 passengers in a Sedan, 6 in an SUV', 'The total for both shown side by side', 'Same professional chauffeurs for every vehicle'],
      },
    ],
    included: INCLUDED.point,
    extras: EXTRAS.point,
    faqTitle: 'Point-to-point questions',
    faq: [
      { q: 'How is a point-to-point ride priced?', a: `By the distance of your route: a Sedan is ${money(sedanRate.base)} plus ${money(sedanRate.perMile)} per mile and an SUV ${money(suvRate.base)} plus ${money(suvRate.perMile)} per mile. The booking page calculates the total for you as soon as you enter both addresses.` },
      { q: 'Is the price fixed once I book?', a: 'Yes. The total you see before you confirm is what you pay, plus any optional tip. Extra charges apply only in the situations in our Terms, such as waiting beyond the free 15 minutes.' },
      { q: 'Is this the same as a black car or limo service?', a: 'Yes. RoolDrive is a private black car service: a professional chauffeur, a luxury Sedan or SUV and a fixed price agreed before the ride. Many people search for it as a limo service or car service; it is the same idea, without the stretch limousine.' },
      { q: 'Can I book a ride for someone else?', a: 'Yes. Book it from your account and add the passenger’s name and phone number in the notes, so the chauffeur knows who to look for and how to reach them.' },
      { q: 'How far ahead should I book?', a: 'Book as early as you can, especially for early mornings, holidays and busy evenings. You can change or cancel free of charge up to 1 hour before pickup.' },
      { q: 'Do you go outside New York City?', a: 'Yes. Point-to-point rides can go to Long Island, Westchester, New Jersey, Connecticut and other cities; the price is calculated the same way, by distance. See our popular routes for examples.' },
    ],
    related: ['/hourly-chauffeur-service', '/flat-rate-car-service', '/nyc-to-new-jersey-car-service', '/nyc-to-stamford-ct-car-service'],
    schema: { name: 'Point-to-point black car service in New York', serviceType: 'Black car service', areaServed: AREA },
  },

  HOURLY && {
    path: '/hourly-chauffeur-service',
    linkText: 'Hourly chauffeur service',
    eyebrow: 'Hourly chauffeur · NYC',
    title: <>Hourly chauffeur service, <em>NYC by the hour</em></>,
    lead: `Your own car and chauffeur for as long as you need, from ${money(HOURLY.sedan.perHour)} an hour. Meetings, shopping, events or a night out: as many stops as you like, one price for your time.`,
    heroImage: 'night-ride-rear-seat',
    from: { sedan: HOURLY.sedan.totals[HOURLY.minHours], suv: HOURLY.suv.totals[HOURLY.minHours] },
    fromNote: `for ${HOURLY.minHours} hours`,
    book: BOOK_HOURLY,
    cta: 'Book an hourly chauffeur',
    introEyebrow: 'Chauffeur as directed',
    introTitle: <>Your time, <em>your stops</em></>,
    intro: [
      'With an hourly booking, also called "as directed" service, a car and chauffeur are yours for a set number of hours. Your chauffeur picks you up, takes you wherever you need to go, waits outside while you are in your meeting, shop or restaurant, and takes you on to the next place. You never wait for a new car and never need to explain the plan to a different driver.',
      `Hourly rides cost ${money(HOURLY.sedan.perHour)} per hour in a Sedan and ${money(HOURLY.suv.perHour)} per hour in an SUV, with a ${HOURLY.minHours}-hour minimum. Each booked hour includes ${HOURLY.sedan.includedMilesPerHour} miles of driving, which covers almost any day in Manhattan, Brooklyn or Queens. Stops are free.`,
      'People book hourly chauffeurs for roadshows and client visits, for a day of appointments, for weddings and galas, for showing family around the city, and for nights out where nobody wants to drive or wait on the street for a ride home.',
    ],
    facts: [
      ['Sedan', `${money(HOURLY.sedan.perHour)} per hour`],
      ['SUV', `${money(HOURLY.suv.perHour)} per hour`],
      ['Minimum', `${HOURLY.minHours} hours`],
      ['Included', `${HOURLY.sedan.includedMilesPerHour} miles per hour`],
    ],
    priceTitle: <>Hourly <em>prices</em></>,
    priceLead: 'Totals for common booking lengths. Book any whole number of hours from the minimum up.',
    priceTables: [{ caption: 'Hourly chauffeur prices by number of hours', rows: hourlyRows(), firstColumn: 'Booked time' }],
    priceNote: `Each hour includes ${HOURLY.sedan.includedMilesPerHour} miles; miles beyond that cost $${HOURLY.sedan.overMileRate} each in a Sedan and $${HOURLY.suv.overMileRate} each in an SUV.`,
    sections: [
      {
        eyebrow: 'Planning your hours',
        title: <>How many hours <em>to book</em></>,
        paragraphs: [
          `Count from your pickup time to the moment you are dropped off for the last time, and add a little room for traffic. A dinner and a show in Midtown is usually 4 to 5 hours. A day of meetings across Manhattan is often 6 to 8. A wedding, with photos at a second location, is typically 4 to 6.`,
          'Plans change. You can add hours on the website or in the app up to 1 hour before pickup. During the ride, if you run more than 15 minutes past the time you booked, the extra time is charged to the card you booked with, as set out in our Terms.',
        ],
        image: 'manhattan-street-night',
        imageAlt: 'A Manhattan street at night seen from a car',
      },
      {
        eyebrow: 'Business and events',
        title: <>Built for <em>busy days</em></>,
        paragraphs: [
          'For business travel, an hourly chauffeur turns a day of back-to-back meetings into something manageable: your bags stay in the car, you can take calls in private between stops, and your chauffeur is waiting when you walk out.',
          'For events, an SUV seats up to 6, so a group can arrive together and leave together. For a night out, it means one safe ride home at the end, whatever time that turns out to be within your booking.',
        ],
        list: ['Roadshows and client meetings', 'Weddings, galas and proms', 'Shopping and city tours', 'Nights out and concerts'],
      },
    ],
    included: INCLUDED.hourly,
    extras: EXTRAS.hourly,
    faqTitle: 'Hourly chauffeur questions',
    faq: [
      { q: 'What is the minimum booking?', a: `${HOURLY.minHours} hours. Shorter bookings are charged as ${HOURLY.minHours} hours, so for a single direct ride a point-to-point booking is usually cheaper.` },
      { q: 'How many miles are included?', a: `${HOURLY.sedan.includedMilesPerHour} miles for every hour you book, so ${HOURLY.sedan.includedMilesAtMinimum} miles on a ${HOURLY.minHours}-hour booking. Miles beyond that are charged at $${HOURLY.sedan.overMileRate} per mile in a Sedan and $${HOURLY.suv.overMileRate} per mile in an SUV.` },
      { q: 'Are stops included?', a: 'Yes. On an hourly ride you can stop as often as you like within your booked time, and your chauffeur waits for you at each stop at no extra charge.' },
      { q: 'What happens if I need more time?', a: 'Add hours on the website or in the app up to 1 hour before pickup. If the ride runs more than 15 minutes past your booked time, the extra time is charged to your card.' },
      { q: 'Can I use an hourly chauffeur for an airport run with errands?', a: 'Yes. If you want stops on the way to or from JFK, LaGuardia or Newark, an hourly booking lets you plan them freely. For a direct airport ride, our flat airport rates are usually better value.' },
      { q: 'Is this the same as a limo by the hour?', a: 'It is the same service, a private chauffeur at your disposal by the hour, in a luxury Sedan or SUV rather than a stretch limousine.' },
    ],
    related: ['/point-to-point-car-service', '/flat-rate-car-service', '/nyc-to-the-hamptons-car-service', '/airport-transfers'],
    schema: {
      name: 'Hourly chauffeur service in New York',
      serviceType: 'Chauffeur service by the hour',
      areaServed: AREA,
      offers: [
        { name: `${VEHICLES.sedan.name}, per hour (${HOURLY.minHours}-hour minimum)`, price: HOURLY.sedan.perHour, unit: 'HOUR' },
        { name: `${VEHICLES.suv.name}, per hour (${HOURLY.minHours}-hour minimum)`, price: HOURLY.suv.perHour, unit: 'HOUR' },
      ],
    },
  },

  flatFrom && jfk && lga && {
    path: '/flat-rate-car-service',
    linkText: 'Flat-rate car service',
    eyebrow: 'Flat-rate car service · NYC',
    title: <>Flat-rate car service, <em>airport to door</em></>,
    lead: 'One fixed price between Manhattan and JFK, LaGuardia or Newark, whatever the time or traffic. Tolls included, no meter, no surge pricing.',
    heroImage: 'airport-terminal',
    from: flatFrom,
    book: BOOK_POINT,
    cta: 'Book a flat-rate ride',
    introEyebrow: 'Fixed-price airport car service',
    introTitle: <>Know the price <em>before you fly</em></>,
    intro: [
      'Our most popular trips have a flat rate. When your pickup is at JFK, LaGuardia or Newark airport and your drop-off is anywhere in Manhattan, or the other way round, the price is fixed in advance. It does not change with traffic on the Van Wyck, a slow tunnel or the time of day.',
      `That means a Sedan from JFK to any Manhattan address is ${money(jfk.sedan)}, whether you are going to Battery Park or Washington Heights. From LaGuardia it is ${money(lga.sedan)}. The booking page recognises the airport and Manhattan from your addresses and applies the flat rate automatically.`,
      'Every other trip is priced by distance and is also shown before you pay, so you always know your total in advance. Flat rates simply take the guesswork out of the trips New Yorkers make most.',
    ],
    facts: [
      ['Applies', 'Airport ↔ anywhere in Manhattan'],
      ['Airports', 'JFK, LaGuardia, Newark'],
      ['Time and traffic', 'Price does not change'],
      ['Free waiting', '60 min after landing'],
    ],
    priceTitle: <>All <em>flat rates</em></>,
    priceLead: 'Every flat-rate route we offer today, by vehicle. Prices are per vehicle, not per passenger.',
    priceTables: [{ caption: 'Flat-rate routes between NYC airports and Manhattan', rows: flatRows, firstColumn: 'Route' }],
    priceNote: 'Flat rates apply when the pickup or drop-off is inside Manhattan and the other end is the airport.',
    sections: [
      {
        eyebrow: 'When the flat rate applies',
        title: <>Direct rides, <em>set zones</em></>,
        paragraphs: [
          'A flat rate covers a direct ride between the airport and Manhattan. If you add a stop on the way, the ride is priced by distance instead, and you see that new price before you pay. Rides from the airports to Brooklyn, Queens, the Bronx or New Jersey are priced by distance; you will find typical prices on each airport page.',
          'Flat rates are per vehicle. Up to 3 passengers travel in a Sedan and up to 6 in an SUV for the same fixed price, which often makes a shared SUV cheaper per person than separate taxis.',
        ],
        image: 'skyline-light-trails',
        imageAlt: 'Manhattan skyline with light trails from traffic at night',
      },
    ],
    included: INCLUDED.airport,
    extras: EXTRAS.airport,
    faqTitle: 'Flat-rate questions',
    faq: [
      { q: 'Which trips have a flat rate?', a: 'Rides between JFK, LaGuardia or Newark airport and any address in Manhattan, in either direction. The table on this page lists every flat-rate route and price.' },
      { q: 'Does the price change with traffic or time of day?', a: 'No. A flat rate is the same at any time of day and in any traffic, and tolls are included. Only the charges in our Terms, such as waiting beyond your free waiting time, can be added.' },
      { q: 'What if my flight is delayed?', a: 'Add your flight number when you book. Your chauffeur tracks the flight, and waiting is free for the first 60 minutes after you land, so a delay does not change your price.' },
      { q: 'Why is my ride to Brooklyn not a flat rate?', a: 'Flat rates are set for the Manhattan zone. Rides to other boroughs are priced by distance, and the total is still shown before you pay.' },
      { q: 'Is the flat rate per person?', a: 'No, it is per vehicle. Everyone travelling together rides for the same price, up to 3 passengers in a Sedan or 6 in an SUV.' },
      { q: 'Can I add a stop and keep the flat rate?', a: 'No. A stop turns the ride into a distance-priced ride. The booking page shows the new total before you confirm, so you can decide.' },
    ],
    related: ['/jfk-car-service', '/laguardia-car-service', '/newark-airport-car-service', '/point-to-point-car-service'],
    schema: { name: 'Flat-rate airport car service in New York', serviceType: 'Airport transfer', areaServed: ['Manhattan', 'JFK Airport', 'LaGuardia Airport', 'Newark Airport'] },
  },
];

export const SERVICE_PAGES = PAGES.filter(Boolean).map((p) => ({ ...p, fromPrice: cheapest(p.from) }));
